import { useMutation, useQuery } from 'urql';
import { graphql } from '../gql/index.ts';
import { Controller, useForm } from 'react-hook-form';
import { VisualSettings } from '../gql/graphql.ts';
import { Button } from '@progress/kendo-react-buttons';
import React, { useEffect, useState } from 'react';
import { Label } from '@progress/kendo-react-labels';
import {
  ColorPicker,
  ColorPickerChangeEvent,
} from '@progress/kendo-react-inputs';
import { FieldWrapper } from '@progress/kendo-react-form';
import { PageTitle } from './PageTitle.tsx';
import { Fieldset } from './form-components/Fieldset.tsx';
import { CheckboxStateWrapper } from './form-components/CheckboxStateWrapper.tsx';
import { TextareaStateWrapper } from './form-components/TextareaStateWrapper.tsx';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { cloneAndRemoveTypename } from '../public/util.ts';
import { useKeycloak } from '../shared/auth/KeycloakProvider.tsx';
import { restApiUrl } from '../shared/helpers/backendUrl';

interface AppearanceSettingsPageProps {
  companyId: string;
}

const CompanyAppearanceQuery = graphql(/* GraphQL */ `
  query companyAppearanceSettings($companyId: String!) {
    company(companyId: $companyId) {
      visualSettings {
        showTutor
        showEndOfRegistrationForSeminar
        showDescription
        showSeminarNumber
        showPrice
        showDate
        showLocation
        showImages
        showSignalLight
        showCalendarEvents
        backToDashboardActive
        backToMainActive
        primaryColor
        primaryTextColor
        customCss
        pathToBanner
      }
    }
  }
`);

const RemoveBannerMutation = graphql(/* GraphQL */ `
  mutation removeBanner($companyId: String!) {
    removeBanner(companyId: $companyId) {
      id
    }
  }
`);

const UpdateCompanyAppearance = graphql(/* GraphQL */ `
  mutation updateCompanyAppearanceSettings(
    $companyId: String!
    $company: UpdateCompanyDto!
  ) {
    updateCompany(companyId: $companyId, company: $company) {
      visualSettings {
        showTutor
        showEndOfRegistrationForSeminar
        showDescription
        showSeminarNumber
        showPrice
        showDate
        showLocation
        showImages
        showSignalLight
        showCalendarEvents
        backToDashboardActive
        backToMainActive
        primaryColor
        primaryTextColor
        customCss
      }
    }
  }
`);

interface FormInputs {
  visualSettings: VisualSettings;
}

export const AppearanceSettingsPage: React.FC<AppearanceSettingsPageProps> = ({
  companyId,
}) => {
  const [{ data, fetching }, reloadCompany] = useQuery({
    query: CompanyAppearanceQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });

  const [, updateCompany] = useMutation(UpdateCompanyAppearance);
  const [, removeBanner] = useMutation(RemoveBannerMutation);
  const { token } = useKeycloak();

  const [bannerFile, setBannerFile] = useState<File | undefined>(undefined);
  const [bannerPreview, setBannerPreview] = useState<string | undefined>(
    undefined,
  );
  const [bannerDirty, setBannerDirty] = useState<boolean>(false);
  const [bannerExistsOnServer, setBannerExistsOnServer] =
    useState<boolean>(false);

  const { control, formState, handleSubmit, reset, setValue } =
    useForm<FormInputs>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        visualSettings: {
          primaryColor: 'rgba(237, 126, 50, 1)',
        },
      },
    });

  const onFormSubmit = async (values: FormInputs) => {
    if (bannerDirty) {
      if (bannerFile) {
        const formData = new FormData();
        formData.append('banner', bannerFile);
        formData.append('companyId', companyId);
        await fetch(restApiUrl('/banner'), {
          method: 'PUT',
          body: formData,
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
      } else if (bannerExistsOnServer) {
        await removeBanner({ companyId });
      }
      setBannerFile(undefined);
      setBannerDirty(false);
    }

    // pathToBanner is read-only (derived from the banner blob on the server)
    // and not part of the VisualSettingsDto input, so it must not be sent.
    const { pathToBanner: _pathToBanner, ...visualSettings } =
      values.visualSettings;
    const r = await updateCompany({
      companyId,
      company: { visualSettings },
    });
    const withoutTypename =
      r.data?.updateCompany && cloneAndRemoveTypename(r.data?.updateCompany);
    if (withoutTypename) {
      reset({ visualSettings: withoutTypename.visualSettings });
    }
    // Refetch so pathToBanner reflects any banner upload/removal.
    reloadCompany({ requestPolicy: 'network-only' });
  };

  useEffect(() => {
    if (!fetching) {
      const withoutTypename =
        data?.company && cloneAndRemoveTypename(data.company);

      if (withoutTypename) {
        reset({ visualSettings: withoutTypename.visualSettings });
        const path = withoutTypename.visualSettings.pathToBanner;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setBannerExistsOnServer(Boolean(path));
        // Only seed the preview from the server when the user hasn't picked a
        // file locally — otherwise we'd clobber an in-progress selection.
        if (!bannerFile) {
          setBannerPreview(path ? restApiUrl(path) : undefined);
        }
      }
    }
  }, [fetching, JSON.stringify(data?.company)]);

  useEffect(() => {
    if (!bannerFile) return;
    const objectUrl = URL.createObjectURL(bannerFile);
    // Object URL must be created in the effect so cleanup can revoke it.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBannerPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [bannerFile]);

  const { isValid, isDirty } = formState;
  const canSave = isValid && (isDirty || bannerDirty);

  return (
    <>
      <PageTitle title={'Darstellung'} />
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="k-form k-form-md relative flex w-full flex-col rounded-md bg-white shadow lg:w-full"
      >
        <Fieldset
          legend="Darstellung"
          description="Beeinflussen Sie die Darstellung Ihrer Seminare auf der öffentlichen Webseite."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <CheckboxStateWrapper
              name={'visualSettings.showTutor'}
              control={control}
              label={'Referierende anzeigen'}
            />
            <CheckboxStateWrapper
              name={'visualSettings.showEndOfRegistrationForSeminar'}
              control={control}
              label={'Anmeldeschluss anzeigen'}
            />
            <CheckboxStateWrapper
              name={'visualSettings.showDescription'}
              control={control}
              label={'Beschreibung anzeigen'}
            />
            <CheckboxStateWrapper
              name={'visualSettings.showSeminarNumber'}
              control={control}
              label={'Seminarnummer anzeigen'}
            />
            <CheckboxStateWrapper
              name={'visualSettings.showPrice'}
              control={control}
              label={'Preis anzeigen'}
            />
            <CheckboxStateWrapper
              name={'visualSettings.showDate'}
              control={control}
              label={'Zeit anzeigen'}
            />
            <CheckboxStateWrapper
              name={'visualSettings.showLocation'}
              control={control}
              label={'Ort anzeigen'}
            />
            <CheckboxStateWrapper
              name={'visualSettings.showImages'}
              control={control}
              label={'Bilder anzeigen'}
            />
            <CheckboxStateWrapper
              name={'visualSettings.showSignalLight'}
              control={control}
              label={'Ampel aktiviert'}
              hint={
                'Sofern weniger als 90% belegt sind und mindestens zwei Plätze frei sind, wird eine grüne Ampel angezeigt.'
              }
            />
            <CheckboxStateWrapper
              name={'visualSettings.showCalendarEvents'}
              control={control}
              label={'Termine in der Seminaransicht anzeigen'}
            />

            <FieldWrapper>
              <Label>
                Primäre Farbe (
                <button
                  type="button"
                  onClick={() =>
                    setValue('visualSettings.primaryColor', '#ff7f51', {
                      shouldDirty: true,
                    })
                  }
                  className="text-[var(--kendo-color-primary)] hover:underline"
                >
                  Zurücksetzen
                </button>
                )
              </Label>
              <Controller
                render={({ field: { onChange, ...rest } }) => (
                  <ColorPicker
                    {...rest}
                    onChange={(event: ColorPickerChangeEvent) => {
                      // Avoid "transparent" value, because it breaks the theming
                      if (event.value) onChange(event.value);
                    }}
                    views={['gradient']}
                  />
                )}
                name={'visualSettings.primaryColor'}
                control={control}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label>
                Primäre Textfarbe (
                <button
                  type="button"
                  onClick={() =>
                    setValue('visualSettings.primaryTextColor', '#000000', {
                      shouldDirty: true,
                    })
                  }
                  className="text-[var(--kendo-color-primary)] hover:underline"
                >
                  Zurücksetzen
                </button>
                )
              </Label>
              <Controller
                render={({ field: { onChange, ...rest } }) => (
                  <ColorPicker
                    {...rest}
                    onChange={(event: ColorPickerChangeEvent) =>
                      onChange(event.value)
                    }
                    views={['gradient']}
                  />
                )}
                name={'visualSettings.primaryTextColor'}
                control={control}
              />
            </FieldWrapper>
          </div>
        </Fieldset>

        <Fieldset
          legend="Bannergrafik"
          description="Der Banner wird auf der öffentlichen Seite stets im Header-Bereich angezeigt. Die ideale Breite dieser Grafik ist 1280 Pixel."
        >
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <div className={'flex flex-col gap-2'}>
              {!bannerPreview && (
                <input
                  type={'file'}
                  id="banner"
                  name="banner"
                  accept={'image/png, image/jpeg'}
                  className="block w-96 cursor-pointer rounded-md border border-gray-200 p-2 file:h-8 file:rounded-md file:border-none file:bg-gray-300 file:p-2"
                  onChange={(e) => {
                    e.preventDefault();
                    setBannerDirty(true);
                    setBannerFile(e.currentTarget?.files?.[0] ?? undefined);
                  }}
                />
              )}
              {bannerPreview && (
                <>
                  <img
                    src={bannerPreview}
                    alt={'Bannergrafik Vorschau'}
                    className="border"
                  />
                  <Button
                    type="button"
                    themeColor={'primary'}
                    fillMode="outline"
                    className="self-start !shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
                    onClick={() => {
                      setBannerFile(undefined);
                      setBannerPreview(undefined);
                      // Only mark dirty when there's something on the server
                      // to remove (or a locally selected file to discard).
                      setBannerDirty(
                        bannerExistsOnServer || Boolean(bannerFile),
                      );
                    }}
                    title="Zurücksetzen"
                  >
                    <HeroIcon name="Trash" className="block h-5 w-5" />
                    <div>Zurücksetzen</div>
                  </Button>
                </>
              )}
            </div>
          </div>
        </Fieldset>

        <Fieldset
          legend="Eigenes CSS"
          description="CSS-Regeln, die zusätzlich auf der öffentlichen Webseite geladen werden (nicht im Adminbereich). Wird unverändert in den Seiten-Head injiziert — fehlerhafte Regeln können die Darstellung beeinträchtigen."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <TextareaStateWrapper
              name={'visualSettings.customCss'}
              control={control}
              rows={20}
              placeholder={'/* z.B. */\n.seminar-card h2 { font-weight: 700; }'}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
            />
          </div>
        </Fieldset>

        <div className="k-form-buttons !mt-0 justify-end rounded-md bg-gray-50 !px-8 !py-3 !text-right">
          <Button
            type={'submit'}
            themeColor={'primary'}
            className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            disabled={!canSave}
            title="Speichern"
          >
            <HeroIcon name="CheckCircle" className="block h-5 w-5" />
            <div>Speichern</div>
          </Button>
        </div>
      </form>
    </>
  );
};
